/**
 * How a form's editors reach the backend, as a visitor rather than an account.
 *
 * A visitor cannot read the related table and has no files of their own, so
 * neither of the two editors that need more than a field can use its usual
 * source. The backend gives them one each, scoped to the form: both are
 * reachable with no session, and both answer only for the field the form
 * actually contains.
 */
import { getBareApi, unwrap } from '@/api/client'
import type { Field } from '@/api/types'
import type { EditorSources } from '../fields/ui/edit/sources'
import type { LinkedRow } from '../fields/ui/edit/link-row'
import type { UserFileValue } from '../files/requests'

/** How many candidates to offer at once. */
const CANDIDATES = 20

export function formSources(slug: string): EditorSources {
  return {
    /**
     * The rows a link row field may point at, already named.
     *
     * A different endpoint from the signed-in one and a better-shaped answer:
     * it returns the id and the primary field's text, which is exactly what a
     * link row cell holds, so nothing here has to know which field of the
     * related table is its primary one.
     */
    linkCandidates: async (field: Field, search: string): Promise<LinkedRow[]> => {
      const answer = await unwrap<{ results?: unknown[] }>(
        getBareApi().GET('/api/database/views/{slug}/link-row-field-lookup/{field_id}/', {
          params: {
            path: { slug, field_id: field.id },
            query: { size: CANDIDATES, ...(search === '' ? {} : { search }) },
          },
        }),
      )

      return ((answer.results ?? []) as LinkedRow[]).map(({ id, value }) => ({
        id,
        value: typeof value === 'string' ? value : '',
      }))
    },

    /**
     * A file, uploaded against the form rather than against an account.
     *
     * Sent as `multipart/form-data`; the client passes a `FormData` through
     * untouched and leaves the content type to the browser, whose boundary it
     * is to write.
     */
    uploadFile: async (file: File): Promise<UserFileValue> => {
      const body = new FormData()
      body.append('file', file)

      const stored = (await unwrap<unknown>(
        getBareApi().POST('/api/database/views/form/{slug}/upload-file/', {
          params: { path: { slug } },
          body: body as never,
        }),
      )) as UserFileValue

      return {
        ...stored,
        visible_name: stored.visible_name ?? stored.original_name ?? '',
      }
    },
  }
}
